import { Box, Typography, type TooltipProps } from '@mui/material';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import {
    labelWithHotkey,
    type ToolEntry,
} from '~/components/toolbar/shared/toolEntry';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';

interface ToolEntryButtonProps {
    entry: ToolEntry;
    tooltipPlacement?: TooltipProps['placement'];
    /** The widest the tooltip grows when it carries the entry's hint, px. */
    hintMaxWidthPx?: number;
    /** Defaults to `tool-<id>`. */
    'data-testid'?: string;
}

/**
 * A toolbar button for one {@link ToolEntry}: a click toggles it, and the
 * tooltip states the refusal while it is disabled, else its name with its
 * key over any usage hint. A disabled button announces its refusal; an
 * enabled one its name, without the key or hint. A held tool is
 * highlighted, and its tooltip and description say it waits for the open
 * operation, while its entry is enabled.
 */
export default function ToolEntryButton({
    entry,
    tooltipPlacement,
    hintMaxWidthPx,
    'data-testid': testId = `tool-${entry.id}`,
}: ToolEntryButtonProps) {
    const intl = useIntl();
    const name = labelWithHotkey(entry);
    const held = entry.held && !entry.disabled;
    const heldNote = held
        ? intl.formatMessage({ id: 'toolbar.tool.heldHint' })
        : undefined;
    const note = heldNote ?? entry.hint;
    let tooltip: ReactNode = name;
    if (entry.disabled) {
        tooltip = buildToolTooltip(intl, entry.label, entry.disabledReason);
    } else if (note) {
        tooltip = (
            <Box sx={{ maxWidth: hintMaxWidthPx }}>
                <Typography fontSize={11}>{name}</Typography>
                <Typography
                    component="span"
                    sx={{ fontSize: 10, opacity: 0.7 }}
                >
                    {note}
                </Typography>
            </Box>
        );
    }
    return (
        <ToolbarButton
            icon={entry.icon}
            tooltip={tooltip}
            ariaLabel={entry.disabled ? undefined : entry.label}
            tooltipPlacement={tooltipPlacement}
            selected={entry.selected}
            flashing={held}
            description={heldNote}
            disabled={entry.disabled}
            onClick={entry.toggle}
            data-testid={testId}
        />
    );
}
